import { useClient } from '@/composables/useClient'
import { HttpError } from '@/queries/auth-error'
import { entityKeys } from '@/queries/entity-keys'
import { mutationOptions } from '@/queries/mutation-options'
import { orderCancelReasonsForDisplay } from '@/queries/transformers/cancelReasons.transformer'
import type {
  CancelReason,
  CancelReasonContext,
  DeliverySlotWindow,
  FeedingPlanServingSize,
  FeedingPlanType,
  Meal,
  ProductType,
  UpdateSubscriptionBodyExtra,
  UpdateSubscriptionBodyFeedingPlanSku,
} from '@lyka-pet-food/contracts'
import type { Subscription } from '@repo/api-types'
import { queryOptions } from '@tanstack/vue-query'

const subscriptionKeys = {
  list: () => [...entityKeys.subscription()] as const,
  cancelReasons: (context: CancelReasonContext | undefined) =>
    [...entityKeys.subscription(), 'cancel-reasons', context] as const,
}

export const subscriptionQueries = {
  list: () =>
    queryOptions({
      queryKey: subscriptionKeys.list(),
      queryFn: async (): Promise<{ subscriptions: Subscription[] }> => {
        const client = await useClient()
        const { status, body } = await client.subscription.getSubscriptions()

        if (status === 200) return body

        throw new HttpError(status, body, 'Could not fetch subscriptions')
      },
    }),

  cancelReasons: (context?: CancelReasonContext) =>
    queryOptions({
      staleTime: Infinity,
      queryKey: subscriptionKeys.cancelReasons(context),
      queryFn: async (): Promise<CancelReason[]> => {
        const client = await useClient()
        const { status, body } = await client.subscription.getCancelReasons({ query: { context } })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to fetch cancel reasons')
      },
      select: orderCancelReasonsForDisplay,
    }),
}

export const subscriptionMutations = {
  reviewUpdateSubscriptionServingSize: () =>
    mutationOptions({
      mutationFn: async ({
        subscriptionId,
        servingSize,
        feedingPlanId,
      }: {
        subscriptionId: string
        servingSize: FeedingPlanServingSize
        feedingPlanId: string
      }) => {
        const client = await useClient()
        const { status, body } = await client.subscription.reviewUpdateSubscriptionServingSize({
          body: { subscriptionId, servingSize, feedingPlanId },
        })

        if (status === 200) return body.data

        throw new HttpError(status, body, 'Failed to review serving size change')
      },
      invalidation: 'skip',
    }),

  updateSubscriptionFeedingPlan: () =>
    mutationOptions({
      mutationFn: async ({
        subscriptionId,
        feedingPlanId,
        servingSize,
      }: {
        subscriptionId: string
        feedingPlanId: string
        servingSize: FeedingPlanServingSize
      }): Promise<Subscription> => {
        const client = await useClient()
        const { status, body } = await client.subscription.updateSubscriptionServingSize({
          body: { subscriptionId, feedingPlanId, servingSize },
        })

        if (status === 200) return body.subscription

        throw new HttpError(status, body, 'Failed to update serving size')
      },
      invalidation: {
        awaited: [{ entity: 'subscription' }],
        background: [{ entity: 'order' }],
      },
    }),

  reviewUpdateSubscriptionFeedingPlanType: () =>
    mutationOptions({
      mutationFn: async ({
        subscriptionId,
        feedingPlanType,
      }: {
        subscriptionId: string
        feedingPlanType: FeedingPlanType
      }) => {
        const client = await useClient()
        const { status, body } = await client.subscription.reviewUpdateSubscriptionFeedingPlanType({
          body: { subscriptionId, feedingPlanType },
        })

        if (status === 200) return body.data

        throw new HttpError(status, body, 'Failed to review the new feeding plan type')
      },
      invalidation: 'skip',
    }),

  updateSubscriptionFeedingPlanType: () =>
    mutationOptions({
      mutationFn: async ({
        subscriptionId,
        feedingPlanType,
      }: {
        subscriptionId: string
        feedingPlanType: FeedingPlanType
      }): Promise<Subscription> => {
        const client = await useClient()
        const { status, body } = await client.subscription.updateSubscriptionFeedingPlanType({
          body: { subscriptionId, feedingPlanType },
        })

        if (status === 200) return body.subscription

        throw new HttpError(status, body, 'Failed to update the feeding plan type')
      },
      invalidation: {
        awaited: [{ entity: 'subscription' }],
        background: [{ entity: 'order' }],
      },
    }),

  updateSubscriptionMeals: () =>
    mutationOptions({
      mutationFn: async ({
        subscriptionId,
        feedingPlanId,
        meals,
      }: {
        subscriptionId: string
        feedingPlanId: string
        meals: UpdateSubscriptionBodyFeedingPlanSku[]
      }): Promise<Subscription> => {
        const client = await useClient()
        const { status, body } = await client.subscription.updateSubscriptionRecipes({
          params: { subscriptionId, feedingPlanId },
          body: meals,
        })

        if (status === 200) return body.subscription

        throw new HttpError(status, body, 'Failed to update subscription meals')
      },
      invalidation: {
        awaited: [{ entity: 'subscription' }],
        background: [{ entity: 'order' }],
      },
    }),

  addSubscriptionExtra: () =>
    mutationOptions({
      mutationFn: async ({
        subscriptionId,
        extraType,
        startOrderId,
        productVariantId,
        nthOrder,
        quantity,
      }: {
        subscriptionId: string
        extraType: ProductType
        startOrderId: string
        productVariantId: string
        nthOrder: number
        quantity: number
      }) => {
        const client = await useClient()
        const { status, body } = await client.subscription.addSubscriptionExtra({
          params: { subscriptionId },
          body: { extraType, startOrderId, productVariantId, nthOrder, quantity },
        })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to add extra to subscription')
      },
      invalidation: { awaited: [{ entity: 'subscription' }, { entity: 'order' }] },
    }),

  updateSubscriptionExtraQuantity: () =>
    mutationOptions({
      mutationFn: async ({
        subscriptionId,
        extraId,
        quantity,
      }: {
        subscriptionId: string
        extraId: string
        quantity: number
      }) => {
        const client = await useClient()
        const { status, body } = await client.subscription.updateSubscriptionExtraQuantity({
          params: { subscriptionId },
          body: { extraId, quantity },
        })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to update subscription extra quantity')
      },
      invalidation: { awaited: [{ entity: 'subscription' }, { entity: 'order' }] },
    }),

  updateSubscriptionExtraFrequency: () =>
    mutationOptions({
      mutationFn: async ({
        subscriptionId,
        extraId,
        startOrderId,
        nthOrder,
        quantity,
        extraType,
        productVariantId,
      }: {
        subscriptionId: string
        extraId: string
        startOrderId: string
        nthOrder: number
        quantity: number
        extraType: ProductType
        productVariantId: string
      }) => {
        const client = await useClient()
        const { status, body } = await client.subscription.updateSubscriptionExtraFrequency({
          params: { subscriptionId },
          body: { extraId, startOrderId, nthOrder, quantity, extraType, productVariantId },
        })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to update subscription extra frequency')
      },
      invalidation: { awaited: [{ entity: 'subscription' }, { entity: 'order' }] },
    }),

  deleteSubscriptionExtra: () =>
    mutationOptions({
      mutationFn: async ({
        subscriptionId,
        extraId,
        cancelReasonId,
        cancelNotes,
      }: {
        subscriptionId: string
        extraId: string
        cancelReasonId?: string
        cancelNotes?: string
      }) => {
        const client = await useClient()
        const { status, body } = await client.subscription.deleteSubscriptionExtra({
          params: { subscriptionId },
          body: { extraId, cancelReasonId, cancelNotes },
        })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to delete subscription extra')
      },
      invalidation: { awaited: [{ entity: 'subscription' }, { entity: 'order' }] },
    }),

  /** @deprecated Use the individual extra crud mutations instead. */
  updateSubscriptionExtras: () =>
    mutationOptions({
      mutationFn: async ({
        subscriptionId,
        extras,
      }: {
        subscriptionId: string
        extras: UpdateSubscriptionBodyExtra[]
      }): Promise<Subscription> => {
        const client = await useClient()
        const { status, body } = await client.subscription.updateSubscriptionExtras({
          params: { subscriptionId },
          body: extras,
        })

        if (status === 200) return body.subscription

        throw new HttpError(status, body, 'Failed to update subscription extras')
      },
      invalidation: { awaited: [{ entity: 'subscription' }, { entity: 'order' }] },
    }),

  cancelSubscription: () =>
    mutationOptions({
      mutationFn: async ({
        subscriptionId,
        primaryCancelReasonId,
        secondaryCancelReasonId,
        otherCancelReason,
      }: {
        subscriptionId: string
        primaryCancelReasonId: string
        secondaryCancelReasonId?: string
        otherCancelReason?: string
      }): Promise<boolean> => {
        const client = await useClient()
        const { status, body } = await client.subscription.cancelSubscription({
          params: { subscriptionId },
          body: { primaryCancelReasonId, secondaryCancelReasonId, otherCancelReason },
        })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to cancel subscription')
      },
      invalidation: { awaited: [{ entity: 'subscription' }, { entity: 'order' }] },
    }),

  reactivateSubscription: () =>
    mutationOptions({
      mutationFn: async ({
        deliveryDate,
        preferredDeliverySlotId,
      }: {
        deliveryDate: string
        preferredDeliverySlotId: DeliverySlotWindow
      }) => {
        const client = await useClient()
        const { status, body } = await client.subscription.reactivateSubscription({
          body: { deliveryDate, preferredDeliverySlotId },
        })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to reactivate subscription')
      },
      invalidation: { awaited: [{ entity: 'subscription' }, { entity: 'order' }] },
    }),

  resumeSubscription: () =>
    mutationOptions({
      mutationFn: async ({
        deliveryDate,
        preferredDeliverySlotId,
      }: {
        deliveryDate: string
        preferredDeliverySlotId: DeliverySlotWindow
      }) => {
        const client = await useClient()
        const { status, body } = await client.subscription.resumeSubscription({
          body: { deliveryDate, preferredDeliverySlotId },
        })

        if (status === 200) return body

        throw new HttpError(status, body, 'Failed to resume subscription')
      },
      invalidation: { awaited: [{ entity: 'subscription' }, { entity: 'order' }] },
    }),

  previewDogRecommendedFeedingPlan: () =>
    mutationOptions({
      mutationFn: async ({ dogId }: { dogId: string }) => {
        const client = await useClient()
        const { status, body } = await client.subscription.previewDogRecommendedFeedingPlan({
          body: { dogId },
        })

        if (status === 200) return body.data

        throw new HttpError(status, body, 'Failed to preview recommended feeding plan')
      },
      invalidation: 'skip',
    }),

  applyDogRecommendedFeedingPlan: () =>
    mutationOptions({
      mutationFn: async ({
        dogId,
        servingSize,
        meals,
      }: {
        dogId: string
        servingSize: number
        meals: { type: Meal; quantity: number }[]
      }) => {
        const client = await useClient()
        const { status, body } = await client.subscription.applyDogRecommendedFeedingPlan({
          body: {
            dogId,
            servingSize,
            meals,
          },
        })

        if (status === 200) return

        throw new HttpError(status, body, 'Failed to apply recommended feeding plan')
      },
      invalidation: {
        awaited: [{ entity: 'subscription' }],
        background: [{ entity: 'order' }],
      },
    }),
}
